import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { TextEntry, TextDbService } from 'services/text-db.service';
import { ENGLISH_TEXT_SEGMENT } from 'services/text-locale';
import { UnsavedChangesGuardProvider, UnsavedChangesGuard } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { AuthService } from 'services/auth.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { ImageService } from 'services/image-service';
import { ScaledImage } from '@cf-shared/data-model/scaled-image';
import { generateIdFromName } from '@cf-shared/shared-util';
import { CsPage, NavController } from 'providers/nav-controller';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { CsTextarea } from 'components/cs-textarea/cs-textarea';
import { Loading, LoadingController } from 'providers/loading-controller';
import { IHelpCardPage } from 'pages/help-card-page/help-card-page-interface';
import { PreviewHelpText } from 'pages/help-card-page/help-card-page';
import { AdminEditTextPageParams } from './admin-edit-text-interface';

@Component({
    selector: 'page-admin-edit-text',
    templateUrl: 'admin-edit-text.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminEditTextPage extends CsPage<AdminEditTextPageParams> {
  text: TextEntry;
  text_org: TextEntry;
  renameAllowed = true;

  unsaved: UnsavedChangesGuard;
  loader: Loading;

  @ViewChild('descriptionTextarea') descriptionRef: CsTextarea;

  constructor(
    public navCtrl: NavController,
    private unsavedProvider: UnsavedChangesGuardProvider,
    private textDb: TextDbService,
    private imageService: ImageService,
    private loadingCtrl: LoadingController,
    public auth: AuthService,
    private alertCtrl: AlertController,
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Dev);

    if (!this.text) {
      let id = this.navParams.data.id;
      if (id) {
        // pin English: this tool edits the AUTHORING document. Without the pin
        // a dev running the app in another language would load — and then save
        // over — the generated translated sibling instead.
        this.text = await this.textDb.getTextByHelpIdAsync(id, undefined, ENGLISH_TEXT_SEGMENT);
        if (!this.text) {
          return this.auth.failPageGuardAsync(`Unknown text id: '${id}'`);
        }
        this.renameAllowed = false;
      } else {
        this.text = {} as TextEntry;
      }
    }

    this.unsaved = this.unsavedProvider.createGuard([this.text], async () => {
      await this.saveAsync();
    });
  }

  async saveAsync() {
    await this.textDb.saveTextAsync(this.text);

    // if the id was just changed (first save)
    let currentId = TextEntry.pouchIdToHelpId(this.text._id);
    if (this.navParams.data.id !== currentId) {
      return this.navCtrl.updateCurrentPageParamsAsync({ id: currentId });
    }
  }

  nameChanged() {
    // if never saved before
    if (this.text._rev == null) {
      // autogenerate id from name
      this.text._id = TextEntry.helpIdToPouchId(generateIdFromName(this.text.title));
    }
  }

  async previewHelpAsync() {
    let previewHelpText: PreviewHelpText = {
      title: untranslatedString(this.text.title), // TODO: admin interface, title should already be TranslatedString
      text: untranslatedString(this.text.text), // TODO: admin interface, text should already be TranslatedString
    }
    window.localStorage.setItem('previewHelpText', JSON.stringify(previewHelpText));

    await this.unsaved.applyChangesAsync();
    void this.navCtrl.pushAsync(IHelpCardPage, {
      helpId: '@help:' + TextEntry.pouchIdToHelpId(this.text._id),
    });
  }

  onImageUploadEvent(event: Event) {
    void this.onImageUploadEventAsync(event);
  }
  async onImageUploadEventAsync(event: Event) {
    const target = event.target as HTMLInputElement;
    const fileName = this.text._id;

    this.loader = this.loadingCtrl.create();
    void this.loader.present();
    // TODO: this is not properly async!!!!
    this.imageService.processUploadImages(
      target.files,
      fileName,
      img => this.addImageToDescription(img),
      err => this.uploadFailed(err),
      true,
    );

    // Resets file input so you can reuse the same image
    // Solution with good browser support:
    // https://stackoverflow.com/questions/20549241/how-to-reset-input-type-file
    target.value = '';
    if (!/safari/i.test(navigator.userAgent)) {
      target.type = '';
      target.type = 'file';
    }
  }

  uploadFailed(err: TranslatedString) {
    void this.loader.dismiss();
    void this.alertCtrl.showAlertAsync({
      title: untranslatedString('Upload failed'),
      message: err,
    });
  }

  addImageToDescription(image: ScaledImage) {
    const cursorIndex = this.descriptionRef.input.nativeElement.selectionStart;
    const markdownTag = this.imageService.getMarkdownTag(image);

    this.descriptionRef.value =
      this.descriptionRef.value.substring(0, cursorIndex) + markdownTag + this.descriptionRef.value.substring(cursorIndex);

    void this.loader.dismiss();
  }

  addImage() {
    document.getElementById('hidden-input').click();
  }

  handleSaveAndExit(event: Event) {
    lockClick(event, this.unsaved.saveAndExitAsync());
  }

  handleDeleteAndExit(event: Event) {
    lockClick(event, this.handleDeleteAndExitAsync());
  }

  private async handleDeleteAndExitAsync() {
    if (!await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Delete text?'),
      message: untranslatedString('Are you sure you want to delete this text?'),
      buttons: [
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel, result: false },
        { text: untranslatedString('Delete'), role: AlertButtonRole.Primary, result: true, },
      ]
    })) {
      return;
    }
    await this.textDb.deleteTextAsync(this.text);
    await this.navCtrl.popAsync();
  }

  async ionViewCanLeave() {
    return this.unsaved.confirmLeaveAsync();
  }
}
